import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { blogPosts } from '@/mocks/blogs';
import { useSEO } from '@/hooks/useSEO';
import ScrollBatteryBar from '@/components/feature/ScrollBatteryBar';
import SiteHeader from '@/components/feature/SiteHeader';
import Footer from '@/pages/home/components/Footer';
import GlowButton from '@/components/base/GlowButton';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

function extractToc(content: string): TocItem[] {
  const lines = content.split('\n');
  const toc: TocItem[] = [];
  lines.forEach((line) => {
    if (line.startsWith('## ')) {
      const text = line.slice(3).trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      toc.push({ id, text, level: 2 });
    } else if (line.startsWith('# ')) {
      const text = line.slice(2).trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      toc.push({ id, text, level: 1 });
    }
  });
  return toc;
}

function TableOfContents({ toc, activeId }: { toc: TocItem[]; activeId: string }) {
  if (toc.length === 0) return null;
  return (
    <div className="bg-[#ddeaf5]/75 backdrop-blur-xl rounded-2xl border border-[#b8d4ea]/60 p-4">
      <h3 className="text-xs font-bold text-slate-600 mb-3 uppercase tracking-wide flex items-center gap-1.5">
        <span className="w-4 h-4 flex items-center justify-center text-[#0088CC]">
          <i className="ri-list-check-2 text-sm"></i>
        </span>
        Contents
      </h3>
      <nav className="flex flex-col gap-0.5">
        {toc.map((item) => {
          const isActive = activeId === item.id;
          return (
            <a
              key={item.id}
              href={`#${item.id}`}
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById(item.id);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs transition-all cursor-pointer group ${
                isActive
                  ? 'bg-[#0088CC]/12 text-[#0088CC] font-semibold'
                  : 'text-slate-500 hover:text-[#0088CC] hover:bg-white/50'
              } ${item.level === 2 ? 'pl-4' : ''}`}
            >
              <span className={`w-1 h-1 rounded-full shrink-0 transition-colors ${isActive ? 'bg-[#0088CC]' : 'bg-slate-300 group-hover:bg-[#0088CC]/50'}`} />
              <span className="leading-snug line-clamp-2">{item.text}</span>
            </a>
          );
        })}
      </nav>
    </div>
  );
}

function renderMarkdown(text: string) {
  const lines = text.split('\n');
  const result: JSX.Element[] = [];
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith('## ')) {
      const text = line.slice(3).trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      result.push(
        <h2 id={id} key={key++} className="text-lg font-bold text-slate-800 mt-8 mb-3 pb-2 border-b border-slate-100 tracking-tight scroll-mt-24">
          {text}
        </h2>
      );
    } else if (line.startsWith('# ')) {
      const text = line.slice(2).trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      result.push(
        <h1 id={id} key={key++} className="text-xl font-extrabold text-slate-800 mt-6 mb-3 tracking-tight scroll-mt-24">
          {text}
        </h1>
      );
    } else if (line.trim() === '') {
      result.push(<div key={key++} className="h-3" />);
    } else {
      // Bold inline
      const parts = line.split(/(\*\*[^*]+\*\*)/g).map((part, pi) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={pi} className="font-bold text-slate-800">{part.slice(2, -2)}</strong>;
        }
        return part;
      });
      result.push(
        <p key={key++} className="text-sm text-slate-600 leading-relaxed">
          {parts}
        </p>
      );
    }
  }
  return result;
}

export default function BlogDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const post = blogPosts.find((p) => p.id === id);
  const related = blogPosts.filter((p) => p.id !== id).slice(0, 3);

  useSEO({
    title: post
      ? `${post.title} | TGDirectory Blog`
      : 'Article Not Found | TGDirectory',
    description: post ? post.excerpt : 'This article could not be found on TGDirectory.',
    keywords: post ? `${post.tags.join(', ')}, Telegram community, ${post.category}` : undefined,
    canonical: post ? `https://tgdirectory.app/blog/${post.id}` : undefined,
    ogType: 'article',
    ogImage: post?.coverImage,
    schema: post
      ? {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: post.title,
          description: post.excerpt,
          image: post.coverImage,
          author: { '@type': 'Person', name: post.author },
          publisher: { '@type': 'Organization', name: 'TGDirectory', url: 'https://tgdirectory.app/' },
          datePublished: post.publishedAt,
          url: `https://tgdirectory.app/blog/${post.id}`,
          keywords: post.tags.join(', '),
        }
      : undefined,
  });

  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post?.likes ?? 0);
  const [progress, setProgress] = useState(0);
  const [activeHeading, setActiveHeading] = useState('');
  const toc = post?.content ? extractToc(post.content) : [];

  // Track active heading via IntersectionObserver
  const observerRef = useRef<IntersectionObserver | null>(null);
  const setupObserver = useCallback(() => {
    observerRef.current?.disconnect();
    if (toc.length === 0) return;
    const headingEls = toc.map((t) => document.getElementById(t.id)).filter(Boolean) as HTMLElement[];
    if (headingEls.length === 0) return;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          setActiveHeading(visible[0].target.id);
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );
    headingEls.forEach((el) => observerRef.current!.observe(el));
  }, [toc]);

  useEffect(() => {
    const t = setTimeout(setupObserver, 300);
    return () => { clearTimeout(t); observerRef.current?.disconnect(); };
  }, [setupObserver, id]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      const article = document.getElementById('article-body');
      if (!article) return;
      const rect = article.getBoundingClientRect();
      const total = article.offsetHeight;
      const scrolled = Math.max(0, -rect.top);
      setProgress(Math.min(100, Math.round((scrolled / total) * 100)));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLike = () => {
    setLiked((prev) => {
      const next = !prev;
      setLikeCount((c) => c + (next ? 1 : -1));
      return next;
    });
  };

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#f5f8fb]">
        <i className="ri-article-line text-4xl text-slate-300 mb-4"></i>
        <p className="text-slate-500 mb-4">Article not found.</p>
        <button onClick={() => navigate('/blogs')} className="text-[#0088CC] text-sm hover:underline cursor-pointer">
          Back to Blogs
        </button>
      </div>
    );
  }

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const formatViews = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f8fb] via-[#f8fafc] to-[#f3f7fa] text-slate-800 font-sans">
      <ScrollBatteryBar />
      <SiteHeader showBack backTo="/blogs" />

      {/* Reading progress bar */}
      <div className="fixed top-0 left-0 z-[60] h-0.5 bg-[#0088CC] transition-all duration-150" style={{ width: `${progress}%` }} />

      <main className="max-w-screen-xl mx-auto px-6 md:px-10 lg:px-14 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-slate-400 mb-6">
          <button onClick={() => navigate('/')} className="hover:text-slate-600 transition-colors cursor-pointer whitespace-nowrap">Home</button>
          <span>/</span>
          <button onClick={() => navigate('/blogs')} className="hover:text-slate-600 transition-colors cursor-pointer whitespace-nowrap">Blogs</button>
          <span>/</span>
          <button
            onClick={() => navigate(`/blogs?category=${encodeURIComponent(post.category)}`)}
            className="text-[#0088CC] font-medium hover:underline cursor-pointer whitespace-nowrap transition-colors"
          >
            {post.category}
          </button>
        </nav>

        <div className="flex gap-8 items-start">
          {/* Article */}
          <article className="flex-1 min-w-0">
            {/* Cover image */}
            <div className="w-full rounded-2xl overflow-hidden mb-7" style={{ height: '360px' }}>
              <img src={post.coverImage} alt={post.title} className="w-full h-full object-cover object-top" />
            </div>

            {/* Meta */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <button
                onClick={() => navigate(`/blogs?category=${encodeURIComponent(post.category)}`)}
                className="text-[10px] font-semibold text-[#0088CC] bg-[#0088CC]/10 hover:bg-[#0088CC]/20 px-2.5 py-0.5 rounded-full uppercase tracking-wider cursor-pointer transition-colors whitespace-nowrap"
              >
                {post.category}
              </button>
              {post.featured && (
                <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 px-2.5 py-0.5 rounded-full border border-amber-200/60">
                  Featured
                </span>
              )}
              {post.tags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => navigate(`/blogs?category=${encodeURIComponent(tag)}`)}
                  className="text-[10px] text-slate-400 hover:text-[#0088CC] bg-slate-100/80 hover:bg-[#0088CC]/8 border border-slate-200/60 hover:border-[#0088CC]/30 px-1.5 py-0.5 rounded-full cursor-pointer transition-colors whitespace-nowrap"
                >
                  #{tag}
                </button>
              ))}
            </div>

            {/* Title */}
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-800 leading-tight mb-4 tracking-tight">
              {post.title}
            </h1>

            {/* Author + stats row */}
            <div className="flex flex-wrap items-center gap-4 mb-7 pb-6 border-b border-slate-200/60">
              <div className="flex items-center gap-2.5">
                <img src={post.authorAvatar} alt={post.author} className="w-10 h-10 rounded-full object-cover object-top border-2 border-white" />
                <div>
                  <p className="text-sm font-bold text-slate-800">{post.author}</p>
                  {post.authorBio && <p className="text-[10px] text-slate-400 line-clamp-1 max-w-xs">{post.authorBio}</p>}
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-400 ml-auto flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {formatDate(post.publishedAt)}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {post.readTime} min read
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formatViews(post.views ?? 0)} views
                </span>
              </div>
            </div>

            {/* Article body */}
            <div id="article-body" className="max-w-2xl">
              {/* Excerpt as lead */}
              <p className="text-base text-slate-600 leading-relaxed font-medium mb-6 border-l-4 border-[#0088CC]/40 pl-4">
                {post.excerpt}
              </p>

              {post.content && (
                <div className="space-y-1">
                  {renderMarkdown(post.content)}
                </div>
              )}
            </div>

            {/* Like / Share bar */}
            <div className="flex items-center gap-3 mt-10 pt-6 border-t border-slate-200/60">
              <button
                onClick={handleLike}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  liked
                    ? 'bg-red-50 border-red-200 text-red-500'
                    : 'bg-white/60 border-slate-200/60 text-slate-500 hover:border-red-200 hover:text-red-500'
                }`}
              >
                <i className={`${liked ? 'ri-heart-fill' : 'ri-heart-line'} text-base`}></i>
                {likeCount}
              </button>
              <button
                onClick={() => navigator.clipboard?.writeText(window.location.href)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200/60 bg-white/60 text-slate-500 hover:border-[#0088CC]/30 hover:text-[#0088CC] text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-line text-base"></i>
                Share
              </button>
              <button
                onClick={() => navigate('/blogs')}
                className="ml-auto flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0088CC] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line text-sm"></i>
                All Articles
              </button>
            </div>

            {/* Author card */}
            <div className="mt-8 bg-white/60 backdrop-blur-md rounded-2xl border border-white/80 p-5 flex items-start gap-4">
              <img src={post.authorAvatar} alt={post.author} className="w-14 h-14 rounded-full object-cover object-top border-2 border-white shrink-0" />
              <div>
                <p className="text-sm font-bold text-slate-800 mb-0.5">{post.author}</p>
                <p className="text-xs text-slate-500 leading-relaxed">{post.authorBio ?? 'Community builder and writer at TGDirectory.'}</p>
              </div>
            </div>

            {/* Related articles */}
            {related.length > 0 && (
              <div className="mt-10">
                <h2 className="text-base font-bold text-slate-700 mb-4">More Articles</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {related.map((r) => (
                    <div
                      key={r.id}
                      onClick={() => navigate(`/blog/${r.id}`)}
                      className="group bg-white/55 backdrop-blur-md rounded-2xl border border-white/80 overflow-hidden cursor-pointer hover:border-[#0088CC]/25 transition-all"
                    >
                      <div className="w-full h-32 overflow-hidden">
                        <img src={r.coverImage} alt={r.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="p-3">
                        <span className="text-[9px] font-semibold text-[#0088CC] bg-[#0088CC]/10 px-1.5 py-0.5 rounded-full uppercase tracking-wider">
                          {r.category}
                        </span>
                        <h3 className="text-xs font-bold text-slate-700 group-hover:text-[#0088CC] transition-colors mt-1.5 line-clamp-2 leading-snug">
                          {r.title}
                        </h3>
                        <p className="text-[10px] text-slate-400 mt-1">{r.readTime} min read</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </article>

          {/* Sticky sidebar */}
          <aside className="hidden xl:flex flex-col w-60 shrink-0 sticky top-20 gap-4">
            {/* Table of Contents */}
            {toc.length > 0 && <TableOfContents toc={toc} activeId={activeHeading} />}

            {/* Reading progress */}
            <div className="bg-white/60 backdrop-blur-md rounded-2xl border border-white/80 p-4">
              <h3 className="text-xs font-bold text-slate-600 mb-2.5 uppercase tracking-wide">Reading Progress</h3>
              <div className="flex items-center gap-2 mb-1.5">
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#0088CC] rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="text-[10px] text-slate-500 font-medium shrink-0">{progress}%</span>
              </div>
              <p className="text-[10px] text-slate-400">{post.readTime} min total read</p>
            </div>

            {/* Quick stats */}
            <div className="bg-[#ddeaf5]/75 backdrop-blur-xl rounded-2xl border border-[#b8d4ea]/60 p-4">
              <h3 className="text-xs font-bold text-slate-600 mb-3 uppercase tracking-wide">Article Stats</h3>
              <div className="flex flex-col gap-2.5">
                {[
                  { icon: 'ri-eye-line', label: 'Views', val: formatViews(post.views ?? 0) },
                  { icon: 'ri-heart-line', label: 'Likes', val: `${likeCount}` },
                  { icon: 'ri-time-line', label: 'Read Time', val: `${post.readTime} min` },
                  { icon: 'ri-calendar-line', label: 'Published', val: formatDate(post.publishedAt) },
                ].map((s) => (
                  <div key={s.label} className="flex items-center gap-2.5">
                    <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#0088CC]/10 shrink-0">
                      <i className={`${s.icon} text-xs text-[#0088CC]`}></i>
                    </span>
                    <div>
                      <p className="text-xs font-bold text-slate-700">{s.val}</p>
                      <p className="text-[10px] text-slate-400">{s.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tags */}
            <div className="bg-white/60 backdrop-blur-md rounded-2xl border border-white/80 p-4">
              <h3 className="text-xs font-bold text-slate-600 mb-2.5 uppercase tracking-wide">Tags</h3>
              <div className="flex flex-wrap gap-1.5">
                {post.tags.map((tag) => (
                  <span key={tag} className="text-[10px] text-slate-500 bg-slate-100/80 border border-slate-200/60 px-2 py-0.5 rounded-full">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Back to blogs */}
            <GlowButton className="w-full text-xs py-2.5 rounded-xl" onClick={() => navigate('/blogs')}>
              <i className="ri-arrow-left-line mr-1.5"></i>
              Back to All Articles
            </GlowButton>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
